import { AgGridReact } from 'ag-grid-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useExperimentSelector } from '../../../../store/selectors/experimentSelector'
import { useCommonApi } from '../../../../store/api/hooks/commonApiHook'
import { useExperimentApi } from '../../../../store/api/hooks/experimentApiHook'
import { useFpLoader } from '../../../../store/api/hooks/fpLoaderHook'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { BusinessFunctionDataType, DistributionColumnNameType } from '../../../../_config/types'
import { ErrorResponse } from '../../../../store/api/apiTypes'
import toast from 'react-hot-toast'
import Text from '../../../../elements/Typo/Text'
import Dropdown from '../../../../elements/Form/dropdown'
import CustomAgGrid from '../../../../components/CustomAgGrid'
import Button from '../../../../elements/button'
import CommonInputText from '../../../../components/commonInputText'
import { createOptionsFromStringArray, filteredOption, getSelectedOption, normalizeRowValues, sortOptions, trackEvent } from '../../../../_common/functions'
import Loader from '../../../../components/loader'
import MeasurementSuccessModal from '../../MeasurementConfigure/modal/measurementSuccess.modal'
import { useDialog } from '../../../../store/api/hooks/dialogHook'
import Label from '../../../../elements/formLabel'
import { cellRenderer } from '../../../../components/cellRenderer'
import { useMeasurementConfigure } from '../../../../_common/hook/useMeasurementConfigure'
import FieldValidation from '../../../../components/groupConfigComponents/fieldValidation'
import { DateFrequency } from '../../MeasurementConfigure'
import i18n from '../../../../i18n'

interface Props {
    tabledata: any
    literal: string
    onContinue: () => void
    onPrevious: () => void
}

const RCTMultiFileConfigure: React.FC<Props> = ({ tabledata, literal, onContinue, onPrevious }) => {
    const { t } = useTranslation()
    const params: any = useParams()
    const dataTableGrid = useRef<AgGridReact>(null)
    const experiment = useExperimentSelector()
    const conmmonApi = useCommonApi()
    const experimentApi = useExperimentApi()
    const { showLoader, hideLoader } = useFpLoader()
    const [kpiInterestsFetched, setKpiInterestsFetched] = useState(false)
    const [KPIRowData, setKPIRowData] = useState<[]>([])
    const [KPIColumnDefs, setKPIColumnDefs] = useState<[]>([])
    const [RecommendationRowData, setRecommendationRowData] = useState<[]>([])
    const [RecommendationColumnDefs, setRecommendationColumnDefs] = useState<[]>([])
    const [KPIColumns, setKPIColumns] = useState<any>(null)
    const [RecommendationColumns, setRecommendationColumns] = useState<any>(null)
    const [kpiInterests, setKpiInterests] = useState<any>(null)
    const [working, setWorking] = useState<boolean>(false)
    const [mdata, setMdata] = useState<any>(null)
    const [groupConfigsReady, setGroupConfigsReady] = useState<boolean>(false)
    const dialog = useDialog()
    const measurementSuccessModalRef: any = useRef()
    const measurementHookFunction = useMeasurementConfigure({ experimentType: 'RCT' })

    const [experimentID, setExperimentID] = useState<string | null>(null)

    const {
        control,
        getValues,
        trigger,
        watch,
        formState: { errors },
    } = useForm()

    const {
        fields: metricFields,
        append: metricAppend,
        remove: metricRemove,
    } = useFieldArray({
        control,
        name: 'metrics',
    })

    const {
        fields: groupConfigs,
        append: groupConfigAppend,
        update: groupConfigUpdate,
    } = useFieldArray({
        control,
        name: 'groupConfigs',
    })

    const granularityWatch = watch('granularity')
    const recGranularityWatch = watch('recommendation')
    const groupConfigsWatch = watch('groupConfigs')
    const metricsWatch = watch('metrics')
    const getKPIColumnsFromResponse = (resp: any) => {
        const all: any = [...resp.data.numeric_columns, ...resp.data.string_columns, ...resp.data.datetime_columns, ...resp.data.boolean_columns, ...resp.data.unique_columns]

        const allCols: any = all.filter((item: string, index: number) => {
            return all.indexOf(item) === index
        })

        const _columns: any = {}
        Object.keys(resp.data).forEach((key: any) => {
            _columns[key as DistributionColumnNameType] = createOptionsFromStringArray(resp.data[key])
        })
        _columns['all'] = allCols.map((name: any) => {
            return {
                label: name,
                value: name,
            }
        })
        setKPIColumns(_columns)
        return _columns
    }

    const getKPIColumns = () => {
        return new Promise((resolve, reject) => {
            experimentApi.getColumnsForGroupDristribution(
                {
                    literal: params.experiment,
                    tableType: 'KPI',
                },
                (resp: any) => {
                    const _columns = getKPIColumnsFromResponse(resp)
                    resolve(_columns)
                },
                (error: any) => {
                    reject(error)
                },
            )
        })
    }

    const getRecommendationColumnsFromResponse = (resp: any) => {
        const _columns: any = {}

        const all: any = [...resp.data.numeric_columns, ...resp.data.string_columns, ...resp.data.datetime_columns, ...resp.data.boolean_columns, ...resp.data.unique_columns]

        const allCols: any = all.filter((item: string, index: number) => {
            return all.indexOf(item) === index
        })
        Object.keys(resp.data).forEach((key: any, i: any) => {
            _columns[key as DistributionColumnNameType] = createOptionsFromStringArray(resp.data[key])
        })
        _columns['all'] = allCols.map((name: any) => {
            return {
                label: name,
                value: name,
            }
        })
        setRecommendationColumns(_columns)
        return _columns
    }

    const getRecommendationColumns = () => {
        return new Promise((resolve, reject) => {
            experimentApi.getColumnsForGroupDristribution(
                {
                    literal: params.experiment,
                    tableType: 'Recommendation',
                },
                (resp: any) => {
                    const _columns = getRecommendationColumnsFromResponse(resp)
                    resolve(_columns)
                },
                (error: any) => {
                    reject(error)
                },
            )
        })
    }

    const getKPITableData = (datacountry: any[] = []) => {
        measurementHookFunction
            .getTableData(experiment)
            .then((data: any) => {
                const columnDefs: any = Object.keys(data?.[0] ?? {}).map((key: any) => {
                    return {
                        headerName: key,
                        field: key,
                        filter: false,
                        sortable: false,
                        suppressMenu: true,
                        cellStyle: {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                        cellRenderer,
                    }
                })
                setKPIColumnDefs(columnDefs)
                setKPIRowData(data)
            })
            .catch((error: any) => {
                console.error('Error :', error)
            })
    }

    const getRecommendationTableData = (datacountry: any[] = []) => {
        let payload = {
            literal: params.experiment,
            page: 1,
            per_page: 40,
            table_type: 'Recommendation',
        }

        if (experiment.experiment_metadatas?.recommendation_table_filters?.filters && Object.keys(experiment.experiment_metadatas?.recommendation_table_filters?.filters).length) {
            const F = { filters: experiment.experiment_metadatas?.recommendation_table_filters?.filters }
            payload = { ...payload, ...F }
        } else if (experiment.experiment_metadatas?.recommendation_table_filters?.sql_query) {
            const S = { sql_query: experiment.experiment_metadatas?.recommendation_table_filters?.sql_query }
            payload = { ...payload, ...S }
        }

        experimentApi.getFilterPreviwData(
            payload,
            (data: any) => {
                const rawResult = data?.data?.result ?? []
                const rows = rawResult.filter((row: any) => row && typeof row === 'object').map(normalizeRowValues)

                if (rows.length === 0) {
                    setRecommendationColumnDefs([] as any)
                    setRecommendationRowData([] as any)
                    return
                }

                const columnDefs: any = Object.keys(rows[0]).map((key: any, i: number) => {
                    return {
                        headerName: key,
                        field: key,
                        filter: false,
                        sortable: false,
                        suppressMenu: true,
                        cellStyle: {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                        cellRenderer,
                    }
                })
                setRecommendationColumnDefs(columnDefs)
                setRecommendationRowData(rows as any)
            },
            (error: any) => {},
        )
    }

    const getKpiTypes = () => {
        conmmonApi.getKpiTypes(
            { sort_by: '-created_at', page: 1, per_page: 10 },
            (data: any) => {
                const interests = structuredClone(data.data.result)?.map((kpi: any) => {
                    kpi.label = kpi.name
                    kpi.value = kpi.kpi_literal
                    return kpi
                })
                setKpiInterests(interests)
                setKpiInterestsFetched(true)
            },
            (error: ErrorResponse<BusinessFunctionDataType>) => {
                toast.error(error.message)
                setKpiInterestsFetched(true)
            },
        )
    }

    const updateExperiment = () => {
        const DATA: any = getValues()

        const payload: any = {
            literal,

            measurement_column_map: {
                granularity: DATA.granularity.value,
                date: DATA.date.value,
                date_frequency: DATA.frequency,
                allocation_group: '',
                recommendation: DATA.recommendation.value,
                kpi: DATA.metrics.map((kpi: any) => {
                    const kpidata = {
                        ...kpi,
                        column_map: kpi.column_map.value,
                    }
                    delete kpidata.id
                    return kpidata
                }),
            },

            recommendation_column_map: {
                granularity: DATA.rec_granularity.value,
                recommendations: DATA.rec_recommendation.value,
                breakdown_level: DATA.breakdown ? DATA.breakdown?.value : '',
                allocation_group: DATA?.grouping ? DATA?.grouping?.value : '',
                kpi_column: DATA?.conversion?.value,
            },
            submit_from: 'measurement_configuration',
        }
        console.log('PAYLOAD=>', payload)
        //return;
        setWorking(true)
        experimentApi.updateExperiment(
            payload,
            (data: any) => {
                experimentApi.executedesignmergeoverview(
                    { literal: params.experiment },
                    (resp: any) => {
                        setWorking(false)
                        setMdata(resp)
                        measurementSuccessModalRef?.current?.showModal()
                    },
                    (error: any) => {
                        setWorking(false)
                    },
                )
            },
            (error: any) => {
                toast.error(error?.message)
            },
        )
    }

    const execute = () => {
        measurementHookFunction.executeMeasurement().catch((error: any) => {
            setWorking(false)
        })
    }

    const updateMuserementExperiment = () => {
        const payload = {
            literal: params.experiment,
            step: 6,
            is_draft: false,
        } //measurementHookFunction.getMeasurementConfigPayload(getValues, experiment)

        setWorking(true)
        experimentApi.updateExperiment(
            payload,
            (resp: any) => {
                experimentApi.executedesignmergeoverview(
                    { literal: params.experiment },
                    (resp: any) => {
                        execute()
                    },
                    (error: any) => {
                        setWorking(false)
                        toast.error(error?.message)
                    },
                )
            },
            (error: any) => {
                setWorking(false)
                toast.error(error?.message)
            },
        )
    }

    const getDistributionDataHandeler = (resp: any) => {
        setWorking(false)
        if (resp.data.groups && resp.data.total_size) {
            if (resp.data.groups?.length < 2 || resp.data.groups?.length > 5) {
                dialog.confirm({
                    type: 'delete',
                    title: t('Validation Error'),
                    message: t('you should not have more than 5 groups or less that 2 groups'),
                    confirmButtonLabel: t('Ok'),
                    cancelButton: false,
                    onConfirm: () => {},
                })
            } else {
                setGroupConfigsReady(true)
                const groups = resp.data.groups
                const totalSize = resp.data.total_size

                const rawDistributions = groups.map((group: any) => (group.size / totalSize) * 100)

                const roundedDistributions = rawDistributions.map((dist: any) => Math.round(dist * 100) / 100)

                const sumRounded = roundedDistributions.reduce((acc: any, val: any) => acc + val, 0)
                const diff = Math.round((100 - sumRounded) * 100) / 100 // 2 decimal places

                roundedDistributions[roundedDistributions.length - 1] += diff

                groups.forEach((group: any, i: number) => {
                    groupConfigAppend({
                        name: group.name,
                        action: '',
                        isControl: false,
                        distribution: roundedDistributions[i],
                        size: group.size,
                    })
                })
            }
        } else {
            console.log('did not get proper response from API')
        }
    }

    const GetMeasurementGroupDistributionData = () => {
        const DATA: any = getValues()
        const payload: any = {
            literal: params.experiment,
            step: 5,
            is_draft: true,
            measurement_column_map: {
                granularity: DATA.granularity.value,
                date: DATA.date.value,
                date_frequency: DATA.frequency,
                allocation_group: '',
                recommendation: DATA.recommendation.value,
                kpi: DATA.metrics.map((kpi: any) => {
                    const kpidata = {
                        ...kpi,
                        column_map: kpi.column_map.value,
                    }
                    delete kpidata.id
                    return kpidata
                }),
            },

            recommendation_column_map: {
                granularity: DATA.rec_granularity?.value,
                recommendations: DATA.rec_recommendation?.value,
                breakdown_level: DATA.breakdown ? DATA.breakdown?.value : '',
                allocation_group: DATA.grouping.value,
                kpi_column: DATA?.conversion?.value,
            },
            submit_from: 'measurement_configuration',
        }

        setWorking(true)
        experimentApi.updateExperiment(
            payload,
            (resp: any) => {
                experimentApi.GetMeasurementGroupDistributionData({ literal: params.experiment, data_type: 'recommendation' }, getDistributionDataHandeler, (error: any) => {
                    console.log('API ---- ERROR')
                    setWorking(false)
                    toast.error(error?.message)
                })
            },
            (error: any) => {
                setWorking(false)
                toast.error(error?.message)
            },
        )
    }
    const getOptionsSelected = () => {
        const a: any = []
        if (granularityWatch) {
            a.push(granularityWatch)
        }
        if (recGranularityWatch) {
            a.push(recGranularityWatch)
        }

        metricsWatch?.forEach((metric: any) => {
            a.push(metric?.column_map?.value)
        })

        return a
    }

    const rec_granularity_watch = watch('rec_granularity')
    const rec_recommendation_watch = watch('rec_recommendation')
    const breakdown_watch = watch('breakdown')
    const grouping_watch = watch('grouping')
    const conversion_watch = watch('conversion')

    const getSelectedRecOption = () => {
        const a: any = []
        if (rec_granularity_watch) {
            a.push(rec_granularity_watch)
        }
        if (rec_recommendation_watch) {
            a.push(rec_recommendation_watch)
        }
        if (breakdown_watch) {
            a.push(breakdown_watch)
        }
        if (grouping_watch) {
            a.push(grouping_watch)
        }
        if (conversion_watch) {
            a.push(conversion_watch)
        }

        return a
    }

    useEffect(() => {
        if (experiment) {
            setExperimentID(experiment?.experiment_literal)
        }
    }, [experiment])
    useEffect(() => {
        if (experimentID) {
            if (
                (experiment?.data_mappings?.delta_measurement_table_name || experiment?.data_mappings?.user_created_measurement_table_name) &&
                (experiment?.data_mappings?.delta_recommendation_table_name || experiment?.data_mappings?.user_created_recommendation_table_name)
            ) {
                getKPITableData()
                getRecommendationTableData()
                showLoader()
                Promise.all([getKPIColumns(), getRecommendationColumns()])
                    .then((values: any) => {
                        setTimeout(hideLoader, 1200)
                    })
                    .catch((error: any) => {
                        hideLoader()
                        toast.error(error?.message || t('Error fetching KPI and Recommendation columns'))
                    })
            }
        }
    }, [experimentID])

    useEffect(() => {
        if (kpiInterests && experiment) {
            if (experiment?.experiment_metadatas?.metrices) {
                metricRemove()
                experiment?.experiment_metadatas?.metrices.map((kpi: any) => {
                    metricAppend(kpi)
                })
            }
        } else if (!kpiInterestsFetched) {
            getKpiTypes()
        }
    }, [experiment, kpiInterests, kpiInterestsFetched])

    const groupConfigIscontrolInput = (name: string, value: boolean, i: number) => {
        return (
            <input
                name={name}
                type="checkbox"
                className="toggle !border-0 text-[#222222] toggle-primary"
                checked={value}
                onChange={(e) => {
                    if (e.target.checked) {
                        trackEvent('measurement_conv_configure_group_control_toggled', { experiment_literal: experiment?.experiment_literal })
                        groupConfigsWatch.map((_group: any, _index: number) => {
                            if (i === _index) {
                                groupConfigUpdate(_index, { ..._group, isControl: true })
                            } else {
                                groupConfigUpdate(_index, { ..._group, isControl: false })
                            }
                        })
                    }
                }}
            />
        )
    }

    return (
        <div data-testid="rct-multi-file-configure">
            <p data-testid="configure-data-title" className="font-bold text-start text-[1.125rem] leading-8">
                {t('Configure Data')}
            </p>

            <Text size="lg1" className="my-2 text-center font-semibold">
                {t('Configure Measurement Data')}
            </Text>
            <div>
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <Text size="15" className="text-black font-medium">
                            {t('Select Granularity')}
                        </Text>
                        <Text size="15" className="!text-[#464E5F] mb-2 italic">
                            {t('Please select the column which represents the Granularity. Ex: POC')} <span className="text-red-500">*</span>
                        </Text>
                    </div>

                    <div>
                        <Text size="15" className="text-black font-medium">
                            {t('Select Date')}
                        </Text>
                        <Text size="15" className="!text-[#464E5F] mb-2 italic">
                            {t('Please select the column which represents the date')} <span className="text-red-500">*</span>
                        </Text>
                    </div>

                    <div>
                        <Text size="15" className="text-black font-medium">
                            {t('Select Granularity for recommendation')}
                        </Text>
                        <Text size="15" className="!text-[#464E5F] mb-2 italic">
                            {t('Please select the column(s) represents the SKU & Brand')} {/* <span className="text-red-500">*</span> */}
                        </Text>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <Controller
                            name="granularity"
                            control={control}
                            rules={{ required: t('Please select') }}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('granularity')
                                        trackEvent('measurement_conv_configure_kpi_granularity_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={filteredOption(KPIColumns ? sortOptions(KPIColumns.all) : [], [], getOptionsSelected())}
                                    error={errors?.granularity?.message as string}
                                    data-testid="rec_granularity-dropdown"
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>

                    <div>
                        <Controller
                            data-testid="date-dropdown"
                            name="date"
                            control={control}
                            rules={{ required: t('Please select') }}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('date')
                                        trackEvent('measurement_conv_configure_kpi_date_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={KPIColumns ? sortOptions(KPIColumns.datetime_columns) : []}
                                    error={errors?.date?.message as string}
                                />
                            )}
                        />

                        <div className="bg-[#FAF8EF] border border-[#0AD4E133] rounded-[0.45rem] mt-2">
                            <div className="flex justify-between p-2">
                                <Text size="lg" className="font-semibold">
                                    {t('Validation:')}
                                </Text>
                                <Text size="lg" className="font-semibold">
                                    {t('Select Date Granularity')}
                                </Text>
                            </div>
                            <div className="border-b border-[#0AD4E133]"></div>
                            <div className="flex items-center justify-between p-1">
                                {metricFields?.length && (metricFields[0] as any).literral_value != 'Total Distribution' ? (
                                    <>
                                        <Text size="md" className="!leading-none">
                                            {t('Select Time Granularity')}
                                        </Text>
                                        {DateFrequency(control, trigger, metricFields, t)}
                                    </>
                                ) : (
                                    <div className="h-6"></div>
                                )}
                            </div>
                            <div className="flex justify-end">{errors?.frequency?.message ? <p className="text-error text-[.542rem] mt-1">{errors?.frequency?.message as string}</p> : null}</div>
                        </div>
                    </div>

                    <div>
                        <Controller
                            data-testid="recommendation-dropdown"
                            name="recommendation"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('recommendation')
                                        trackEvent('measurement_conv_configure_kpi_recommendation_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={filteredOption(KPIColumns ? sortOptions(KPIColumns.all) : [], [], getOptionsSelected())}
                                    error={errors?.recommendation?.message as string}
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>
                </div>
            </div>

            {kpiInterests && (
                <div className="mt-4 grid grid-cols-2 gap-3">
                    <div>
                        <div>
                            <Text size="md" className="text-black font-medium">
                                {t('Select KPIs Column')}
                            </Text>
                            <Text size="md" className="text-[#464E5F] mb-2 italic">
                                {t('The columns which represent the primary and other KPIs on which you want to execute Measurement. Please ensure to select the relevant numerical columns only.')} <span className="text-red-500">*</span>
                            </Text>
                        </div>

                        {kpiInterests && (
                            <>
                                {metricFields.map((kpi: any, i: number) => (
                                    <div key={`kpi-${kpi.id}`} className="grid grid-cols-2 gap-2 mb-2">
                                        <CommonInputText value={getSelectedOption(kpi.kpi_literal, kpiInterests)?.label} disabled={true} />
                                        <Controller
                                            name={`metrics.${i}.column_map`}
                                            control={control}
                                            rules={{
                                                required: t('Please select'),
                                            }}
                                            render={({ field: { value, onChange } }) => (
                                                <Dropdown
                                                    value={value}
                                                    onChange={(value: any) => {
                                                        onChange(value)
                                                        void trigger(`metrics.${i}`)
                                                        trackEvent('measurement_conv_configure_kpi_mapped', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                                    }}
                                                    options={filteredOption(KPIColumns ? sortOptions(KPIColumns?.numeric_columns) : [], [], getOptionsSelected())}
                                                    error={(errors?.metrics as any)?.[i]?.column_map?.message as string}
                                                />
                                            )}
                                        />
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            )}

            <p className="text-[1rem] font-bold mt-4">{t('KPI data')}</p>

            <div className="mt-1 mb-4">
                <CustomAgGrid
                    key={'rctMConf1' + i18n.language}
                    gridRef={dataTableGrid}
                    rowData={KPIRowData}
                    columnDefs={KPIColumnDefs}
                    defaultColDef={{ sortable: true, filter: true }}
                    theme="light"
                    pagination={false}
                    paginationPageSize={5} // Must match one of the values below
                    paginationPageSizeSelector={[5, 10, 20, 50]} // Ensure 5 is included
                    style={{ height: 350, width: '100%' }}
                    gridId="summmyGrid"
                    rowHeight={50}
                    alternativeRowColor={'#E5B61112'}
                />
            </div>

            <Text size="lg1" className="my-2 text-center font-semibold">
                {t('Configure Recommendation Data')}
            </Text>

            <div>
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <Controller
                            name="rec_granularity"
                            control={control}
                            rules={{ required: t('Please select') }}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    label={t('Select Granularity')}
                                    required
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('rec_granularity')
                                        trackEvent('measurement_conv_configure_rec_granularity_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={filteredOption(sortOptions(RecommendationColumns?.all) || [], [], getSelectedRecOption())}
                                    error={errors?.rec_granularity?.message as string}
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>

                    <div>
                        <Controller
                            name="rec_recommendation"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    label={t('Select Granularity for recommendation (SKU,Brand)')}
                                    isClearable
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('rec_recommendation')
                                        trackEvent('measurement_conv_configure_rec_recommendation_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={filteredOption(sortOptions(RecommendationColumns?.all) || [], [], getSelectedRecOption())}
                                    error={errors?.rec_recommendation?.message as string}
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>

                    <div>
                        <Controller
                            name="breakdown"
                            control={control}
                            rules={{ required: t('Please select') }}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    label={t('Select Additional Breakdown Level')}
                                    value={value}
                                    required
                                    isMulti={false}
                                    isClearable={true}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('breakdown')
                                        trackEvent('measurement_conv_configure_rec_breakdown_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={filteredOption(sortOptions(RecommendationColumns?.all) || [], [], getSelectedRecOption())}
                                    error={errors?.breakdown?.message as string}
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>

                    <div data-testid="grouping-dropdown-wrapper">
                        <Controller
                            name="grouping"
                            control={control}
                            rules={{
                                required: experiment?.is_measurement_flow ? t('Please select') : false,
                            }}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    testId="grouping-dropdown"
                                    label={t('Select Grouping Column')}
                                    required={experiment?.is_measurement_flow || false}
                                    value={value}
                                    isClearable={true}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('grouping')
                                        trackEvent('measurement_conv_configure_rec_grouping_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={filteredOption(sortOptions(RecommendationColumns?.all) || [], [], getSelectedRecOption())}
                                    error={errors?.grouping?.message as string}
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>

                    <div data-testid="conversion-dropdown-wrapper">
                        <Controller
                            name="conversion"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    label={t('Select Conversion Column')}
                                    value={value}
                                    onChange={(value: any) => {
                                        onChange(value)
                                        void trigger('conversion')
                                        trackEvent('measurement_conv_configure_rec_conversion_selected', { experiment_literal: experiment?.experiment_literal, column_name: value?.label })
                                    }}
                                    options={filteredOption(sortOptions(RecommendationColumns?.boolean_columns) || [], [], getSelectedRecOption())}
                                    error={errors?.conversion?.message as string}
                                />
                            )}
                        />

                        <FieldValidation />
                    </div>
                </div>
            </div>

            <p className="mt-4 text-[1rem] font-bold">{t('Recommendation Data:')}</p>

            <div className="mt-2" data-testid="recommendation-grid-container">
                <CustomAgGrid
                    key={'rctMconf2' + i18n.language}
                    dataTestId="recommendation-grid"
                    gridRef={dataTableGrid}
                    rowData={RecommendationRowData}
                    columnDefs={RecommendationColumnDefs}
                    defaultColDef={{ sortable: true, filter: true }}
                    theme="light"
                    pagination={false}
                    paginationPageSize={5} // Must match one of the values below
                    paginationPageSizeSelector={[5, 10, 20, 50]} // Ensure 5 is included
                    style={{ height: 350, width: '100%' }}
                    gridId="summmyGrid"
                    rowHeight={50}
                    alternativeRowColor={'#E5B61112'}
                />
            </div>

            {groupConfigsReady && (
                <div className="max-w-[40rem] mx-auto mt-6">
                    <div className="grid grid-cols-9 gap-2">
                        <div className="col-span-4 grid grid-cols-2 gap-2">
                            <Label text={t('Group Name')} className="text-center w-full" />
                            <Label text={t('Action Taken')} />
                        </div>
                        <div className="col-span-2 flex items-center justify-center">
                            <Label text={t('Mark one of the Groups as Control')} />
                        </div>

                        <div className="col-span-3 grid grid-cols-3 gap-2 items-center">
                            <div className="col-span-2">
                                <Label text={t('Distribution%')} />
                            </div>
                            <div className="flex items-center">
                                <Label text={t('Group Size')} />
                            </div>
                        </div>
                    </div>

                    {groupConfigs?.map((group: any, i: any) => (
                        <div className="grid grid-cols-9 gap-2 mb-2 " key={`group-${group.id}`}>
                            <div className="col-span-4 bg-[#f4f4f4] border border-[#d4d4d4] rounded-[0.3rem] pl-[0.417rem] grid grid-cols-2 gap-2">
                                <div className="flex items-center ">
                                    <Text size="md" className="leading-none font-semibold">
                                        {group.name}
                                    </Text>
                                </div>
                                <div className="flex items-center  ">
                                    <Controller
                                        name={`groupConfigs[${i}].action`}
                                        control={control}
                                        rules={{
                                            required: t('Please provide action'),
                                            minLength: {
                                                value: 2,
                                                message: t('Action should be atleast 2 character'),
                                            },
                                            maxLength: {
                                                value: 1000,
                                                message: t('Maximum 1000 character allowed'),
                                            },
                                            validate: {
                                                notOnlyNumbers: (value) => {
                                                    return /^(?=.*[a-zA-Z]).*$/.test(value) ? true : t('Action should not contain only numerical or only special character.')
                                                },
                                            },
                                        }}
                                        render={({ field: { value, onChange } }) => (
                                            <CommonInputText
                                                multiline={true}
                                                value={value}
                                                onChange={(e) => {
                                                    onChange(e)
                                                    void trigger(`groupConfigs[${i}].action`)
                                                }}
                                                placeholder={t('Action')}
                                                error={(errors?.groupConfigs as any)?.[i]?.action?.message as string}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="col-span-2 flex items-center justify-center bg-[#f4f4f4] border border-[#d4d4d4] rounded-[0.3rem] p-[0.417rem]">
                                <Controller name={`groupConfigs[${i}].isControl` as 'groupConfigs.0.isControl'} control={control} render={({ field: { name, value } }) => groupConfigIscontrolInput(name, value, i)} />
                            </div>
                            <div className="col-span-3 bg-[#f4f4f4] border border-[#d4d4d4] rounded-[0.3rem] p-1 grid grid-cols-3 gap-2 items-center">
                                <div className="col-span-2">
                                    <Text size="md" className="text-center font-semibold flex-grow">
                                        {group.distribution}%
                                    </Text>
                                </div>
                                <div className="flex items-center">
                                    <Text size="md" className="text-center font-semibold flex-grow">
                                        {group.size}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-4">
                <Button
                    theme="secondary"
                    size="xs"
                    styleType="outline"
                    onClick={() => {
                        trackEvent('measurement_conv_configure_previous_clicked', { experiment_literal: experiment?.experiment_literal })
                        if (onPrevious) {
                            onPrevious()
                        }
                    }}
                    data-testid="previous-button"
                >
                    {t('Previous Step')}
                </Button>

                {experiment?.is_measurement_flow ? (
                    <Button
                        theme="secondary"
                        size="xs"
                        onClick={() => {
                            trackEvent('measurement_conv_configure_submitted', { experiment_literal: experiment?.experiment_literal })
                            trigger()
                                .then((valid: boolean) => {
                                    if (valid) {
                                        if (groupConfigsReady) {
                                            updateMuserementExperiment()
                                        } else {
                                            GetMeasurementGroupDistributionData()
                                        }
                                    } else {
                                        console.log('Not Valid', errors)
                                    }
                                })
                                .catch((error) => {
                                    console.error('Error :', error)
                                })
                        }}
                        disabled={working}
                    >
                        {working ? <Loader /> : t('Continue')}
                    </Button>
                ) : (
                    <Button
                        theme="secondary"
                        size="xs"
                        onClick={() => {
                            trackEvent('measurement_conv_configure_submitted', { experiment_literal: experiment?.experiment_literal })
                            measurementHookFunction.checkBeforeMeasurementStart(trigger, updateExperiment)
                        }}
                        disabled={working}
                        data-testid="submit-button"
                    >
                        {working ? <Loader /> : t('Measure Result')}
                    </Button>
                )}
            </div>
            <MeasurementSuccessModal _ref={measurementSuccessModalRef} mdata={mdata} updateExperiment={true} />
        </div>
    )
}

export default RCTMultiFileConfigure
